/**
 * The smoke bomb a deleted message leaves behind.
 *
 * The cloud is drawn over the page, not inside the card, so the card can leave
 * the list in the same frame as the click and the smoke still plays where it
 * was. Nothing waits on it: it removes itself when it has finished.
 */
import { el } from "./render";

const PUFFS = 22;
const DURATION_MS = 650;

export function poof(target: Element): void {
  const box = target.getBoundingClientRect();
  if (box.width === 0 || box.height === 0) return;

  const cloud = el("div", { class: "poof", "aria-hidden": "true" });
  Object.assign(cloud.style, {
    left: `${box.left}px`, top: `${box.top}px`,
    width: `${box.width}px`, height: `${box.height}px`,
  });
  document.body.append(cloud);

  const animations: Animation[] = [];
  if (matchMedia("(prefers-reduced-motion: reduce)").matches) {
    const ghost = el("i", { class: "poof-ghost" });
    cloud.append(ghost);
    animations.push(ghost.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, easing: "ease-out" }));
  } else {
    for (let i = 0; i < PUFFS; i++) {
      const size = 44 + Math.random() * 60;
      const puff = el("i", { class: "poof-puff" });
      Object.assign(puff.style, {
        width: `${size}px`, height: `${size}px`,
        left: `${Math.random() * box.width - size / 2}px`,
        top: `${box.height / 2 + (Math.random() - 0.5) * box.height - size / 2}px`,
      });
      cloud.append(puff);
      const drift = (Math.random() - 0.5) * 60;
      const rise = -20 - Math.random() * 40;
      animations.push(puff.animate(
        [
          { opacity: 0, transform: "scale(0.3)" },
          { opacity: 0.85, transform: "scale(1)", offset: 0.25 },
          { opacity: 0, transform: `translate(${drift}px, ${rise}px) scale(1.7)` },
        ],
        { duration: DURATION_MS * (0.7 + Math.random() * 0.5), delay: Math.random() * 90, easing: "ease-out" },
      ));
    }
  }
  void Promise.allSettled(animations.map((a) => a.finished)).then(() => cloud.remove());
}

/**
 * Close the gap a removed card left, rather than jumping the next card into
 * the smoke. The list is already redrawn without it; this is a spacer at the
 * same place that shrinks to nothing.
 */
export function closeGap(list: Element | null, index: number, height: number): void {
  if (!list || height <= 0 || matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const spacer = el("div", { class: "poof-gap", "aria-hidden": "true" });
  list.insertBefore(spacer, list.children[index] ?? null);
  const gap = parseFloat(getComputedStyle(list).rowGap) || 0;
  spacer
    .animate([{ height: `${height}px`, marginBottom: "0px" }, { height: "0px", marginBottom: `${-gap}px` }],
      { duration: 280, delay: 120, easing: "cubic-bezier(.4,0,.2,1)", fill: "forwards" })
    .finished.finally(() => spacer.remove());
}
